<template>
  <div :class="ns.b()">
    <lt-icon v-if="isShowArrow" :class="[ns.m(props.type)]" @click="scrollUp">
      <ArrowUp />
    </lt-icon>
    <div
      :class="[`state-${props.type}`, ns.e('content')]"
      @wheel.prevent="onScroll"
      @mousedown="startDrag"
      @mouseup="stopDrag"
      @mouseleave="stopDrag"
    >
      <div
        :class="[ns.e('scroll-content')]"
        :style="{ transform: `translateY(${scrollPosition}px)` }"
      >
        <div
          v-for="(item, index) in displayedOptions"
          :key="item.value"
          :class="[
            ns.e('scroll-item'),
            ns.e('hover-color'),
            { active: item.value === modelValue, disabled: item.disabled },
          ]"
          @mouseover="hoveredItem = item"
          @mouseleave="hoveredItem = null"
          @click="selectOption(index, item)"
        >
          {{ item.label || '-' }}
        </div>
      </div>
    </div>
    <lt-icon v-if="isShowArrow" :class="[ns.m(props.type)]" @click="scrollDown">
      <ArrowDown />
    </lt-icon>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import { LtIcon } from '@lit-element/components/icon'
import { useNamespace } from '@lit-element/hooks'
import { ArrowDown, ArrowUp } from '@inte-cloud/icons-vue'
import { wheelProps } from './wheel'

defineOptions({
  name: 'LtWheel',
})
const ns = useNamespace('wheel')
const props = defineProps(wheelProps)
const currentValue = ref(props.modelValue)
const scrollPosition = ref(0)
const hoveredItem = ref<any>(null) // 当前悬停的项目

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'handleChange', value: object): void
}>()

const updateCurrentValue = (index: number) => {
  const newValue = displayedOptions.value[index].value
  currentValue.value = newValue
  scrollPosition.value =
    -index * props.itemHeight +
    Math.floor(props.visibleItems / 2) * props.itemHeight
  emit('update:modelValue', newValue || '') // 确保传递的值不为 undefined
}

// 初始化位置
const initializeScrollPosition = () => {
  const initialIndex = displayedOptions.value.findIndex(
    (item) => item.value === currentValue.value
  )
  if (initialIndex !== -1) {
    scrollPosition.value =
      -initialIndex * props.itemHeight +
      Math.floor(props.visibleItems / 2) * props.itemHeight
  } else {
    // 如果没有找到匹配的值，默认选中空值
    const emptyIndex = displayedOptions.value.findIndex(
      (item) => item.value === props.insertEmpty
    )
    if (emptyIndex !== -1) {
      updateCurrentValue(emptyIndex)
    }
  }
}

const onScroll = (event: WheelEvent) => {
  const direction = event.deltaY < 0 ? -1 : 1
  const currentIndex = displayedOptions.value.findIndex(
    (item) => item.value === currentValue.value
  )
  let newIndex = currentIndex + direction
  // 跳过禁用的选项
  while (newIndex >= 0 && newIndex < displayedOptions.value.length) {
    if (!displayedOptions.value[newIndex].disabled) {
      updateCurrentValue(newIndex)
      break
    }
    newIndex += direction
  }
}

let isDragging = false
let startY = 0

const startDrag = (event: MouseEvent) => {
  isDragging = true
  startY = event.clientY
}

const stopDrag = () => {
  isDragging = false
}

const onMouseMove = (event: MouseEvent) => {
  if (!isDragging) return

  const deltaY = Math.floor((startY - event.clientY) / props.itemHeight)

  if (deltaY !== 0) {
    const currentIndex = displayedOptions.value.findIndex(
      (item) => item.value === currentValue.value
    )
    let newIndex = currentIndex + deltaY

    while (newIndex >= 0 && newIndex < displayedOptions.value.length) {
      if (!displayedOptions.value[newIndex].disabled) {
        updateCurrentValue(newIndex)
        startY = event.clientY // 更新起始位置以继续拖动
        break
      }
      newIndex += deltaY
    }
  }
}

const scrollUp = () => {
  const currentIndex = displayedOptions.value.findIndex(
    (item) => item.value === currentValue.value
  )
  let newIndex = Math.max(0, currentIndex - 1)

  while (newIndex >= 0) {
    if (!displayedOptions.value[newIndex].disabled) {
      updateCurrentValue(newIndex)
      break
    }
    newIndex--
  }
}

const scrollDown = () => {
  const currentIndex = displayedOptions.value.findIndex(
    (item) => item.value === currentValue.value
  )
  let newIndex = Math.min(displayedOptions.value.length - 1, currentIndex + 1)

  while (newIndex < displayedOptions.value.length) {
    if (!displayedOptions.value[newIndex].disabled) {
      updateCurrentValue(newIndex)
      break
    }
    newIndex++
  }
}

const displayedOptions = computed(() => {
  const options = props.option || []
  // 将props.option中 转化为label和value组合
  // if (props.aaa) {
  //   function transformArray(arr, { key, value }) {
  //     return arr.map((item) => ({
  //       label: item[key],
  //       value: item[value],
  //     }));
  //   }
  //   options = transformArray(props.option, props.aaa);
  // }
  // 如果空值存在，则在最前面插入一个空值

  if (props.insertEmpty) {
    return [{ label: props.insertEmpty, value: props.insertEmpty }, ...options]
  }

  return options
})

onMounted(() => {
  initializeScrollPosition()
  document.addEventListener('mousemove', onMouseMove)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
})

watch([toRef(props, 'option'), toRef(props, 'insertEmpty')], () => {
  initializeScrollPosition()
})

// 点击选择选项
const selectOption = (index: number, item: object) => {
  if (!props.selectable) return
  updateCurrentValue(index)
  emit('handleChange', item)
}
</script>
